import { Karte } from "@/components/ui"
import { jahrLabel, type AuswertungsDaten } from "@/lib/auswertung"
import { mmss, nf0, nf1 } from "@/lib/format"

const GRUENDE: Array<{ k: string; name: string; farbe: string }> = [
  { k: "unvollstaendig", name: "unvollständig oder unlesbar", farbe: "rgb(var(--muted))" },
  { k: "fehlzeile", name: "Kopf- oder Endzeile", farbe: "rgb(var(--muted))" },
  { k: "grenzwert", name: "außerhalb der Grenzwerte", farbe: "#d03b3b" },
  { k: "ausreisser", name: "statistischer Ausreißer", farbe: "rgb(var(--warn))" },
]

export function Datenqualitaet({ d }: { d: AuswertungsDaten }) {
  const summe = (k: string) => d.qualitaet.reduce((a, e) => a + (e[k] ?? 0), 0)
  const gz = d.meta.grenzen
  const methode: Array<[string, string]> = [
    ["Kennzahl", "Durchsatz NC feucht je Probe = Auswurfgewicht ÷ Zykluszeit in Sekunden × 3600. Der Kennwert einer Gruppe ist der Mittelwert der Proben (wie in den Excel-Pivots) oder wahlweise der Median."],
    ["Zykluszeit", "Im Tagebuch als min:sec eingetragen, von Excel aber als Uhrzeit gelesen: „07:36:00“ bedeutet 7:36 min = 456 s. Die Umrechnung erfolgt direkt; Schreibweisen wie „006.45“ oder „0010:10“ werden erkannt. Am Terminal wird die Zykluszeit direkt als min:sec erfasst."],
    ["Jahr", "Jahr des Betriebstagebuchs (Datum der Probe), nicht die ersten Ziffern der Mischungsnummer."],
    ["Grenzwerte", `Zykluszeit ${mmss(gz.zykluszeit_s[0])} bis ${mmss(gz.zykluszeit_s[1])} min, Auswurfgewicht ${gz.auswurfgewicht_kg[0]} bis ${gz.auswurfgewicht_kg[1]} kg, Füllgewicht ${gz.fuellgewicht_kg[0]} bis ${gz.fuellgewicht_kg[1]} kg, Auswurf nicht größer als Füllung. Werte außerhalb sind Tipp- oder Spaltenfehler und bleiben immer ausgeschlossen.`],
    ["Ausreißer", `Je Fahrweise (NC-Typ × Gebinde × Medium × Anfeuchtung Soll, ab ${d.meta.tukey.min_n} Proben) gilt eine Probe als Ausreißer, wenn ihr Durchsatz mehr als das ${nf1.format(d.meta.tukey.k)}-Fache des Quartilsabstands unter dem unteren oder über dem oberen Quartil liegt. Sie sind standardmäßig ausgeblendet und per Schalter einblendbar.`],
  ]
  for (const tb of d.meta.letzter_import?.hinweise ?? []) {
    if (!tb.jahr) continue
    methode.push([`Betriebstagebuch ${tb.jahr}`, `Aus den Monatsblättern von „${tb.datei}“ gelesen, Stand ${tb.bis ?? "–"}. ${nf0.format(tb.ohne_datum)} Proben stehen in Tagesblöcken ohne eingetragenes Datum; sie erhalten das Datum des vorherigen Blocks bzw. den Monat des Blatts. ${nf0.format(tb.dubletten)} Zeilen standen doppelt in zwei Monatsblättern und zählen einmal.`])
  }
  const th = "px-2 py-1.5 text-right text-[11.5px] font-medium text-muted whitespace-nowrap"
  const td = "border-t border-grid px-2 py-1.5 text-right whitespace-nowrap"
  return (
    <Karte titel="Datenqualität und Methode" unter="Gilt für alle Proben, unabhängig von den Filtern.">
      <div className="flex flex-wrap gap-x-7 gap-y-2.5">
        <div className="grid gap-0.5"><b className="text-xl font-semibold">{nf0.format(summe("gesamt"))}</b><span className="text-xs text-ink2">Proben insgesamt</span></div>
        <div className="grid gap-0.5"><b className="text-xl font-semibold">{nf0.format(summe("ok"))}</b><span className="text-xs text-ink2">ausgewertet</span></div>
        {GRUENDE.map((g) => (
          <div key={g.k} className="grid gap-0.5">
            <b className="text-xl font-semibold">{nf0.format(summe(g.k))}</b>
            <span className="inline-flex items-center gap-1.5 text-xs text-ink2"><i className="inline-block h-2 w-2 rounded-full" style={{ background: g.farbe }} />{g.name}</span>
          </div>
        ))}
      </div>
      <div className="min-w-0 overflow-x-auto">
        <table className="tabular w-full border-collapse text-[12.5px]">
          <thead><tr><th className={`${th} text-left`}>Jahr</th><th className={th}>Proben</th><th className={th}>ausgewertet</th>{GRUENDE.map((g) => <th key={g.k} className={th}>{g.name}</th>)}</tr></thead>
          <tbody>
            {d.qualitaet.map((e) => (
              <tr key={e.jahr}>
                <td className={`${td} text-left`}>{jahrLabel(d, e.jahr)}</td>
                <td className={td}>{nf0.format(e.gesamt)}</td>
                <td className={td}>{nf0.format(e.ok ?? 0)}</td>
                {GRUENDE.map((g) => <td key={g.k} className={td}>{nf0.format(e[g.k] ?? 0)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="m-0 grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(150px,0.3fr)_minmax(0,1fr)]">
        {methode.map(([dt, dd]) => (
          <div key={dt} className="contents">
            <dt className="text-[13px] font-semibold">{dt}</dt>
            <dd className="m-0 max-w-[78ch] text-ink2">{dd}</dd>
          </div>
        ))}
      </dl>
      <h3 className="text-[15px] font-semibold">Größte Abweichungen</h3>
      <div className="min-w-0 overflow-x-auto">
        <table className="tabular w-full border-collapse text-[12.5px]">
          <thead>
            <tr>{["Grund", "Datum", "Mischung", "NC-Typ", "Gebinde", "Medium", "Zykluszeit (Eintrag)", "Auswurf kg", "Füll kg", "Durchsatz kg/h", "Fundstelle"].map((s) => <th key={s} className={`${th} text-left`}>{s}</th>)}</tr>
          </thead>
          <tbody>
            {d.beispiele.map((e, i) => (
              <tr key={i}>
                <td className={`${td} text-left`}>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface2 px-2 py-0.5 text-[11.5px] text-ink2">
                    <i className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: e.grund.startsWith("stat") ? "rgb(var(--warn))" : "#d03b3b" }} />
                    {e.grund}
                  </span>
                </td>
                <td className={`${td} text-left`}>{e.datum}</td>
                <td className={`${td} text-left font-mono text-xs`}>{e.mischung}</td>
                <td className={`${td} text-left font-mono text-xs`}>{e.typ}</td>
                <td className={`${td} text-left`}>{e.geb}</td>
                <td className={`${td} text-left`}>{e.med}</td>
                <td className={`${td} text-left font-mono text-xs`}>{e.zyklus}</td>
                <td className={td}>{e.aw == null ? "–" : nf1.format(e.aw)}</td>
                <td className={td}>{e.f == null ? "–" : nf1.format(e.f)}</td>
                <td className={td}>{e.tp == null ? "–" : nf0.format(e.tp)}</td>
                <td className={`${td} text-left text-muted`}>{e.quelle}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  )
}
